/**
 * Tajweed rule precedence and teaching content — §8.1, §8.2, L17.
 *
 * L17: a QCF glyph encodes an ENTIRE WORD, so CSS cannot colour letters inside it.
 * Per-word mode must therefore pick ONE rule. Measured over the corpus: 40.6% of
 * words carry no rule, 41.6% exactly one, and 17.8% carry two or more — so a
 * tie-break is needed for roughly one word in six.
 *
 * The order runs madd family first (they change how long you hold a sound) down to
 * merely-silent. This ordering is the one part of the feature that is a judgement
 * call, which is why per-word mode is presented as an approximation and the KFGQPC
 * colour font is offered as the faithful option.
 */
import type { MushafPage, MushafWord, TajweedRun } from './types'

export const TAJWEED_RULE_ORDER = [
  'madda_necessary',
  'madda_obligatory',
  'madda_permissible',
  'madda_normal',
  'qalaqah',
  'ghunnah',
  'iqlab',
  'ikhafa',
  'ikhafa_shafawi',
  'idgham_ghunnah',
  'idgham_wo_ghunnah',
  'idgham_shafawi',
  'idgham_mutajanisayn',
  'idgham_mutaqaribayn',
  'laam_shamsiyah',
  'ham_wasl',
  'slnt',
] as const

export type TajweedRuleId = (typeof TAJWEED_RULE_ORDER)[number]

const PRECEDENCE = new Map<string, number>(TAJWEED_RULE_ORDER.map((id, i) => [id, i]))

/**
 * `end` appears in the run data but is the ayah-number glyph, not a rule. It must
 * never be offered as a legend chip or picked as a dominant colour.
 */
export const NON_RULE_TOKENS = new Set(['end'])

export const isRuleId = (id: string): id is TajweedRuleId => PRECEDENCE.has(id)

/** Every rule occurring in a set of runs, in precedence (and therefore teaching) order. */
export function rulesInRuns(runs: readonly TajweedRun[] | undefined): TajweedRuleId[] {
  if (!runs) return []
  const seen = new Set<TajweedRuleId>()
  for (const run of runs) {
    for (const id of run.slice(1)) {
      if (isRuleId(id)) seen.add(id)
    }
  }
  return TAJWEED_RULE_ORDER.filter((id) => seen.has(id))
}

/**
 * The ayah's own text, end marker dropped — the ONE owner of "what counts as ayah
 * text" for copy, share, or any other plain-text extraction from runs.
 *
 * All 6,236 ayat carry the ayah-number glyph as their FINAL run, tagged `end`, and
 * carry exactly one (pinned in `integrity.test.ts`). Joining runs blindly therefore
 * bakes the number inside the ﴿…﴾ of a copied ayah, where the verse key beside it
 * already says the same thing. The run before it ends in a space, hence the trim.
 *
 * Filtering belongs HERE, at the text-extraction boundary: the same runs feed the
 * tajweed colouring path and must not be mutated to achieve this.
 */
export function ayahTextFromRuns(runs: readonly TajweedRun[] | undefined): string {
  if (!runs) return ''
  return runs
    .filter((run) => !run.slice(1).some((id) => NON_RULE_TOKENS.has(id)))
    .map((run) => run[0])
    .join('')
    .trim()
}

/** The single rule per-word mode paints the word with, or null if it carries none. */
export function dominantRule(word: Pick<MushafWord, 'tj'>): TajweedRuleId | null {
  let best: TajweedRuleId | null = null
  let bestRank = Infinity
  for (const run of word.tj ?? []) {
    for (const id of run.slice(1)) {
      const rank = PRECEDENCE.get(id)
      if (rank !== undefined && rank < bestRank) {
        bestRank = rank
        best = id as TajweedRuleId
      }
    }
  }
  return best
}

// ---------------------------------------------------------------------------
// Teaching cards (§8.1). The card's WORDING lives in the reader
// (`apps/web/src/content/tajweed.ts`) and is still shared by the study panel and
// /tajweed through one component, so it cannot drift between them.
// ---------------------------------------------------------------------------

export interface TajweedExemplar {
  verseKey: string
  /**
   * 1-based word position within the ayah. The exemplar is stored as a POINTER into
   * the corpus, never as a transcribed string: Arabic copied out of a rendered view
   * comes back subtly different (this actually happened while writing this file), and
   * a hardcoded word would then silently fail to match the data it claims to quote.
   * `resolveExemplar` reads the real word off the page.
   */
  position: number
}

/**
 * A card is a POINTER AND A PRECEDENCE, not a lesson.
 *
 * I3: the teaching prose — `howTo`, `when`, `commonError`, `searchQuery` — is
 * user-facing Arabic and moved to `apps/web/src/content/tajweed.ts`. What a pure
 * package can own is the part that is a fact about the corpus, and the exemplar pointer
 * is exactly that.
 */
export interface TajweedRuleCard {
  id: TajweedRuleId
  exemplar: TajweedExemplar
  /**
   * A pinned lesson. NULL for every rule here: no video URL in this build has been
   * verified against a live source, and a card that ships a dead embed is worse than
   * one that ships none. The reader's `searchQuery` drives the «المزيد من الدروس»
   * link instead, which can never go dead.
   */
  lesson: string | null
}

/**
 * Every exemplar below was mined from the shipped corpus, never written from memory.
 * `tajweed.test.ts` asserts that the word at each {verseKey, position} really does
 * carry its rule in the data we ship — so a card can never quote a word that does not
 * demonstrate the rule it teaches.
 */
export const TAJWEED_CARDS: Record<TajweedRuleId, TajweedRuleCard> = {
  madda_necessary: {
    id: 'madda_necessary',
    exemplar: { verseKey: '1:7', position: 9 },
    lesson: null,
  },
  madda_obligatory: {
    id: 'madda_obligatory',
    exemplar: { verseKey: '78:1', position: 2 },
    lesson: null,
  },
  madda_permissible: {
    id: 'madda_permissible',
    exemplar: { verseKey: '1:1', position: 4 },
    lesson: null,
  },
  madda_normal: {
    id: 'madda_normal',
    exemplar: { verseKey: '1:1', position: 3 },
    lesson: null,
  },
  qalaqah: {
    id: 'qalaqah',
    exemplar: { verseKey: '78:6', position: 2 },
    lesson: null,
  },
  ghunnah: {
    id: 'ghunnah',
    exemplar: { verseKey: '78:1', position: 1 },
    lesson: null,
  },
  iqlab: {
    id: 'iqlab',
    exemplar: { verseKey: '80:16', position: 1 },
    lesson: null,
  },
  ikhafa: {
    id: 'ikhafa',
    exemplar: { verseKey: '78:12', position: 4 },
    lesson: null,
  },
  ikhafa_shafawi: {
    id: 'ikhafa_shafawi',
    exemplar: { verseKey: '79:14', position: 2 },
    lesson: null,
  },
  idgham_ghunnah: {
    id: 'idgham_ghunnah',
    exemplar: { verseKey: '78:13', position: 2 },
    lesson: null,
  },
  idgham_wo_ghunnah: {
    id: 'idgham_wo_ghunnah',
    exemplar: { verseKey: '78:36', position: 3 },
    lesson: null,
  },
  idgham_shafawi: {
    id: 'idgham_shafawi',
    exemplar: { verseKey: '83:14', position: 5 },
    lesson: null,
  },
  idgham_mutajanisayn: {
    id: 'idgham_mutajanisayn',
    exemplar: { verseKey: '109:4', position: 5 },
    lesson: null,
  },
  idgham_mutaqaribayn: {
    id: 'idgham_mutaqaribayn',
    exemplar: { verseKey: '4:158', position: 1 },
    lesson: null,
  },
  laam_shamsiyah: {
    id: 'laam_shamsiyah',
    exemplar: { verseKey: '78:18', position: 4 },
    lesson: null,
  },
  ham_wasl: {
    id: 'ham_wasl',
    exemplar: { verseKey: '1:1', position: 2 },
    lesson: null,
  },
  slnt: {
    id: 'slnt',
    exemplar: { verseKey: '78:27', position: 2 },
    lesson: null,
  },
}

/**
 * Read the exemplar word off a real mushaf page. The card stores a pointer, so the
 * word it shows is by construction the word the data marks.
 */
export function resolveExemplar(
  page: Pick<MushafPage, 'lines'>,
  exemplar: TajweedExemplar,
): MushafWord | null {
  for (const line of page.lines) {
    for (const word of line.words) {
      if (word.verse_key === exemplar.verseKey && word.position === exemplar.position) return word
    }
  }
  return null
}

/**
 * A YouTube search link that can never go dead (§8.1) — unlike a pinned video id.
 * Takes the query rather than the card: the query is Arabic copy and lives in the
 * reader (I3), the URL shape is not.
 */
export const lessonSearchUrl = (searchQuery: string): string =>
  `https://www.youtube.com/results?search_query=${encodeURIComponent(searchQuery)}`

/**
 * The five merely-orthographic rules. §14.3 paints these in `--ink-faint` rather than
 * a hue of their own: they do not change how long you hold a sound.
 */
export const ORTHOGRAPHIC_RULES = new Set<TajweedRuleId>([
  'ham_wasl',
  'slnt',
  'laam_shamsiyah',
  'idgham_mutajanisayn',
  'idgham_mutaqaribayn',
])
